/**
 * ImportResults Component
 *
 * Displays the results of a bulk import operation.
 * Shows success and error counts with detailed lists of records.
 *
 * Features:
 * - Summary statistics (success/error counts)
 * - List of successfully imported records
 * - List of failed records with error messages
 * - Limits displayed records to 10 with "N more" indicator
 * - Type-specific display (orgUnits vs locations)
 *
 * @module ImportExportTab/components/ImportResults
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const MAX_DISPLAYED_RECORDS = 10;

/**
 * ImportResults Component
 *
 * @param {Object} props
 * @param {Object} props.results - Import results from bulkImportService
 * @param {Array} props.results.success - Successfully imported records
 * @param {Array} props.results.errors - Failed records with errors
 * @param {Object} props.results.summary - Summary statistics
 * @param {string} props.importType - Type of import ("orgUnits" or "locations")
 * @returns {JSX.Element}
 */
function ImportResults({ results, importType }) {
  const { success, errors, summary } = results;

  const displayedSuccess = success.slice(0, MAX_DISPLAYED_RECORDS);
  const remainingSuccess = success.length - MAX_DISPLAYED_RECORDS;

  const displayedErrors = errors.slice(0, MAX_DISPLAYED_RECORDS);
  const remainingErrors = errors.length - MAX_DISPLAYED_RECORDS;

  /**
   * Format record display name
   */
  const getRecordName = (record) => {
    if (importType === 'locations') {
      return record.locationName || 'Unknown Location';
    }
    return record.name || 'Unknown OrgUnit';
  };

  /**
   * Format record details
   */
  const getRecordDetails = (record) => {
    if (importType === 'locations') {
      const parts = [];
      if (record.city) parts.push(record.city);
      if (record.state) parts.push(record.state);
      return parts.join(', ') || 'No details';
    }
    return record.orgLevel || 'No org level';
  };

  return (
    <Box>
      {/* Summary Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${summary.successCount} successful`}
          color="success"
          sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
        />
        <Chip
          icon={<ErrorIcon />}
          label={`${summary.errorCount} failed`}
          color={summary.errorCount > 0 ? 'error' : 'default'}
          sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
        />
      </Box>

      {/* Success Records */}
      {summary.successCount > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              Successfully Imported ({summary.successCount})
            </Typography>

            <List dense>
              {displayedSuccess.map((item, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={getRecordName(item.record)}
                    secondary={getRecordDetails(item.record)}
                  />
                </ListItem>
              ))}
            </List>

            {remainingSuccess > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                + {remainingSuccess} more records successfully imported
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error Records */}
      {summary.errorCount > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" />
              Failed Imports ({summary.errorCount})
            </Typography>

            <List dense>
              {displayedErrors.map((item, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemIcon>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={getRecordName(item.record)}
                      secondary={
                        <>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {getRecordDetails(item.record)}
                          </Typography>
                          <Typography variant="caption" display="block" color="error">
                            Error: {item.error}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < displayedErrors.length - 1 && <Divider variant="inset" component="li" />}
                </Box>
              ))}
            </List>

            {remainingErrors > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                + {remainingErrors} more errors
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

ImportResults.propTypes = {
  results: PropTypes.shape({
    success: PropTypes.arrayOf(PropTypes.object).isRequired,
    errors: PropTypes.arrayOf(PropTypes.object).isRequired,
    summary: PropTypes.shape({
      totalRecords: PropTypes.number.isRequired,
      successCount: PropTypes.number.isRequired,
      errorCount: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  importType: PropTypes.oneOf(['orgUnits', 'locations']).isRequired
};

export default ImportResults;
